import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useSwipeable } from 'react-swipeable';
import { ActivityCardSkeleton } from 'components/RecentActivityCarousel/ActivityCardSkeleton';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { Points } from 'components/Points/Points';
import UserHoverCard from 'components/UserHoverCard/UserHoverCard';
import ResultCard from 'screens/Main/Search/Components/ResultCard';
import { RECENT_ACTIVITY_CAROUSEL_COUNT, RECENT_ACTIVITY_COUNT } from 'constants/home';
import { useActivityCarousel } from 'hooks/useActivityCarousel';
import { useHoverCard } from 'hooks/useHoverCard';
import { useIsMobile } from 'hooks/useIsMobile';
import { usePrefersReducedMotion } from 'hooks/usePrefersReducedMotion';
import { Activity, getActivityActionText } from 'types/interfaces/activity';
import { trackEvent } from 'utils/analytics';
import { formatAccessibleTime, formatRelativeTime } from 'utils/relativeTime';
import { CarouselNavigation } from './CarouselNavigation';
import { feedCardToTile } from './feedCardToTile';
import './RecentActivitySection.css';

// Recent Activity, full width. Each tile is the Search page's card tile (Asana
// 1219036410957400, Decision 1: same import, no copy): hover flips to the
// details face, click opens the shared action menu. The feed keeps its own
// header row above the card (actor, verb) and its own meta row under it (the
// event's points and relative time, never the tile's "N offering").
//
// Desktop pages through the feed's 54 items one page at a time (Asana
// 1219377729700824): the page size is the grid's column count (8 / 6 / 4 by
// breakpoint), arrows and swipe step one page, no auto-advance. Phones keep
// the single swipe row at RECENT_ACTIVITY_COUNT tiles.

/** Grid columns by breakpoint; must mirror the `.home-activity__row` media queries. */
const PAGE_SIZE_QUERIES: Array<[string, number]> = [
  ['(max-width: 900px)', 4],
  ['(max-width: 1100px)', 6],
];
const PAGE_SIZE_DESKTOP = 8;

const readPageSize = (): number => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return PAGE_SIZE_DESKTOP;
  for (const [query, size] of PAGE_SIZE_QUERIES) {
    if (window.matchMedia(query)?.matches) return size;
  }
  return PAGE_SIZE_DESKTOP;
};

/** Live page size, tracked the way useIsMobile tracks its breakpoint. */
const useActivityPageSize = (): number => {
  const [size, setSize] = useState(readPageSize);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const lists = PAGE_SIZE_QUERIES.map(([query]) => window.matchMedia(query)).filter(Boolean);
    const onChange = () => setSize(readPageSize());
    onChange();
    lists.forEach((mql) => (mql.addEventListener ? mql.addEventListener('change', onChange) : mql.addListener(onChange)));
    return () =>
      lists.forEach((mql) =>
        mql.removeEventListener ? mql.removeEventListener('change', onChange) : mql.removeListener(onChange),
      );
  }, []);
  return size;
};

const SLIDE_MS = 150;

/**
 * The trader header of a tile: avatar, username and verb, linking to the
 * profile. It carries the same UserHoverCard wiring as every other username
 * surface (Leaderboard, Send, Receive, Inbox), which is why it is a component:
 * the hover hook is one per trigger and the tiles render in a map.
 */
const ActivityActor: React.FC<{
  activity: Activity;
  actionText: string;
  onClick: (e: React.MouseEvent) => void;
}> = ({ activity, actionText, onClick }) => {
  const { triggerProps, cardProps, isVisible, position, data, isLoading } = useHoverCard(
    activity.user.username,
  );
  return (
    <>
      <Link
        to={`/profile/u/${encodeURIComponent(activity.user.username)}`}
        className="home-activity__actor"
        onClick={onClick}
        {...triggerProps}
      >
        <AvatarWithFlag
          avatarUrl={activity.user.avatarUrl}
          username={activity.user.username}
          countryCode={activity.user.countryCode}
          size={30}
          isElite={activity.user.isElite}
          showAnimatedBorder={activity.user.showAnimatedBorder}
          borderColor1={activity.user.borderColor1}
          borderColor2={activity.user.borderColor2}
        />
        <span className="home-activity__actor-text">
          <span className="home-activity__username">
            {activity.user.username}
            <EliteBadge isElite={!!activity.user.isElite} />
          </span>
          <span className="home-activity__verb">{actionText}</span>
        </span>
      </Link>
      {isVisible && (
        <UserHoverCard data={data} isLoading={isLoading} position={position} cardProps={cardProps} />
      )}
    </>
  );
};

const RecentActivitySection: React.FC = () => {
  const { activities, isLoading, error, refetch } = useActivityCarousel({ enableSocket: true });
  const isMobile = useIsMobile();
  const pageSize = useActivityPageSize();
  const reducedMotion = usePrefersReducedMotion();

  const live = useMemo(
    () => activities.slice(0, isMobile ? RECENT_ACTIVITY_COUNT : RECENT_ACTIVITY_CAROUSEL_COUNT),
    [activities, isMobile],
  );

  // Paging. While the user is past page 1 the list is frozen (`held`) so a
  // socket prepend cannot shift the tiles under the cursor; the cache keeps
  // accumulating and the snapshot is dropped on the way back to page 1, which
  // then shows the new items first as it always has.
  const [page, setPage] = useState(1);
  const [held, setHeld] = useState<Activity[] | null>(null);
  const [slide, setSlide] = useState<'left' | 'right' | null>(null);
  const items = held ?? live;
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    if (isMobile) {
      setPage(1);
      setHeld(null);
    }
  }, [isMobile]);

  const goToPage = useCallback(
    (next: number, direction: 'left' | 'right') => {
      const target = Math.min(Math.max(next, 1), totalPages);
      if (target === page) return;
      if (target === 1) setHeld(null);
      else setHeld((current) => current ?? live);
      setPage(target);
      setSlide(reducedMotion ? null : direction);
      trackEvent('carousel_arrow_clicked', { direction: direction === 'left' ? 'prev' : 'next', page: target });
    },
    [live, page, reducedMotion, totalPages],
  );
  const goPrev = useCallback(() => goToPage(page - 1, 'left'), [goToPage, page]);
  const goNext = useCallback(() => goToPage(page + 1, 'right'), [goToPage, page]);

  useEffect(() => {
    if (!slide) return;
    const t = window.setTimeout(() => setSlide(null), SLIDE_MS);
    return () => window.clearTimeout(t);
  }, [slide, page]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => goNext(),
    onSwipedRight: () => goPrev(),
    delta: 50,
    trackMouse: false,
  });

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    }
  };

  const visible = useMemo(
    () => (isMobile ? items : items.slice((page - 1) * pageSize, page * pageSize)),
    [isMobile, items, page, pageSize],
  );
  const paged = !isMobile && totalPages > 1;

  // One hover face and one open menu at a time, owned here exactly as
  // ResultsGrid owns them on Search. Keyed by the feed item id, which is
  // unique per event even when two events show the same printing.
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  const handleUserClick = (activity: Activity) => (e: React.MouseEvent) => {
    e.stopPropagation();
    trackEvent('carousel_username_clicked', {
      user_id: activity.user.id,
      username: activity.user.username,
      activity_type: activity.type,
    });
  };

  const rowClass = ['home-activity__row', slide ? `home-activity__row--slide-${slide}` : '']
    .filter(Boolean)
    .join(' ');

  return (
    <section className="home-activity" data-testid="recent-activity-section" aria-label="Recent activity">
      <div className="home-activity__header">
        <h2 className="home-activity__title">Recent Activity</h2>
        <Link to="/home/activity" className="home-activity__more">
          View more
        </Link>
      </div>

      {error ? (
        <div className="home-activity__empty">
          <p>Unable to load recent activity.</p>
          <button type="button" className="home-activity__retry" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className="home-activity__row" data-testid="activity-row">
          <ActivityCardSkeleton count={RECENT_ACTIVITY_COUNT} />
        </div>
      ) : visible.length === 0 ? (
        <div className="home-activity__empty" data-testid="activity-empty">
          <p>No recent activity yet. Be the first to start trading!</p>
        </div>
      ) : (
        <div
          className="home-activity__viewport"
          data-testid="activity-viewport"
          data-page={page}
          data-total-pages={totalPages}
          {...(paged ? { ...swipeHandlers, tabIndex: 0, onKeyDown, 'aria-roledescription': 'carousel' } : {})}
        >
          {paged && (
            <CarouselNavigation
              direction="left"
              onClick={goPrev}
              disabled={page <= 1}
              ariaLabel="Previous activity"
            />
          )}
          <div key={page} className={rowClass} data-testid="activity-row" data-count={visible.length}>
            {visible.map((activity) => {
              const key = activity.id;
              const actionText = getActivityActionText(activity.type);
              return (
                <article
                  key={key}
                  className="home-activity__tile"
                  data-testid="home-activity-tile"
                  data-activity-id={key}
                  aria-label={`${activity.user.username} ${actionText} ${activity.card.name}`}
                >
                  <ActivityActor activity={activity} actionText={actionText} onClick={handleUserClick(activity)} />

                  <ResultCard
                    card={feedCardToTile(activity)}
                    isHovered={hoveredKey === key}
                    onHover={() => setHoveredKey(key)}
                    onLeave={() => setHoveredKey(null)}
                    isMobile={isMobile}
                    isMenuOpen={openMenuKey === key}
                    onOpenMenu={() => {
                      setOpenMenuKey(key);
                      setHoveredKey(key);
                    }}
                    onCloseMenu={() => setOpenMenuKey(null)}
                    origin="home"
                    meta={
                      <>
                        {activity.pointValue !== null ? (
                          <Points points={activity.pointValue} type="small" />
                        ) : (
                          <span className="result-card-unpriced">???</span>
                        )}
                        <time
                          className="home-activity__time"
                          dateTime={activity.createdAt}
                          title={formatAccessibleTime(activity.createdAt)}
                        >
                          {formatRelativeTime(activity.createdAt)}
                        </time>
                      </>
                    }
                  />
                </article>
              );
            })}
          </div>
          {paged && (
            <CarouselNavigation
              direction="right"
              onClick={goNext}
              disabled={page >= totalPages}
              ariaLabel="Next activity"
            />
          )}
          {paged && (
            <>
              <div className="home-activity__dots" aria-hidden="true">
                {Array.from({ length: totalPages }, (_, i) => (
                  <span
                    key={i}
                    className={`home-activity__dot${i + 1 === page ? ' home-activity__dot--active' : ''}`}
                  />
                ))}
              </div>
              <span className="home-activity__sr-only" aria-live="polite">
                Page {page} of {totalPages}
              </span>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default RecentActivitySection;
